import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { useLanguage } from "./LanguageContext";

// Clean icon components without external dependencies
const DownloadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
    <polyline points="7,10 12,15 17,10"></polyline>
    <line x1="12" y1="15" x2="12" y2="3"></line>
  </svg>
);

const FileTextIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14,2 L6,2 C4.9,2 4,2.9 4,4 L4,20 C4,21.1 4.9,22 6,22 L18,22 C19.1,22 20,21.1 20,20 L20,8 L14,2 Z"></path>
    <polyline points="14,2 14,8 20,8"></polyline>
    <line x1="16" y1="13" x2="8" y2="13"></line>
    <line x1="16" y1="17" x2="8" y2="17"></line>
    <polyline points="10,9 9,9 8,9"></polyline>
  </svg>
);

const CogIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3"></circle>
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1 1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
  </svg>
);

const FolderOpenIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
    <path d="M2 19l6.5-9h12.5"></path>
  </svg>
);

const ShieldIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
  </svg>
);

const ReceiptIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1-2-1Z"></path>
    <path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8"></path>
    <path d="M12 18V6"></path>
  </svg>
);

const ArrowUpRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="7" y1="17" x2="17" y2="7"></line>
    <polyline points="7,7 17,7 17,17"></polyline>
  </svg>
);

interface ReportesProps {
  currentPage?: string;
}

export function Reportes({ currentPage = "reportes" }: ReportesProps = {}) {
  const { getBilingualText, language } = useLanguage();

  const renderFinanciero = () => {
    const handleReporteClick = (url: string) => {
      window.open(url, '_blank');
    };

    return (
      <div className="min-h-screen bg-gray-50">
        <div className="p-6 space-y-8">
          {/* Header fijo */}
          <div className="space-y-2">
            <h1 className="text-2xl font-semibold text-black">
              {getBilingualText("reports.financial.title")}
            </h1>
            <p className="text-muted-foreground">
              {getBilingualText("reports.financial.subtitle")}
            </p>
          </div>

          {/* Tabla de reportes */}
          <div className="space-y-6">
            {/* Header de la tabla */}
            <Card className="bg-white rounded-2xl border border-gray-100 shadow-sm">
              <CardContent className="p-0">
                <div className="overflow-hidden">
                  {/* Header */}
                  <div className="bg-gray-50 p-6 border-b border-gray-100">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center flex-shrink-0">
                        <FileTextIcon />
                      </div>
                      <div>
                        <h2 className="text-xl font-semibold text-black">
                          {getBilingualText("reports.financial.title")}
                        </h2>
                        <p className="text-muted-foreground">
                          {getBilingualText("reports.financial.subtitle")}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Tabla */}
                  <div className="divide-y divide-gray-100">
                    {/* Fila Q1 2025 */}
                    <div 
                      className="p-6 hover:bg-gray-50 cursor-pointer transition-colors duration-200 group"
                      onClick={() => handleReporteClick('https://drive.google.com/drive/folders/12L7p8sebBWHHYOtKTfW60-GjTySvw-yD?usp=sharing')}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 group-hover:bg-gray-200 transition-colors">
                            <FolderOpenIcon />
                          </div>
                          <div className="space-y-1">
                            <h3 className="font-semibold text-black">Q1 2025</h3>
                            <p className="text-sm text-muted-foreground">
                              Enero - Marzo 2025
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-sm text-gray-500">PDF & Excel</span>
                          <ArrowUpRightIcon />
                        </div>
                      </div>
                    </div>

                    {/* Fila Q2 2025 */}
                    <div 
                      className="p-6 hover:bg-gray-50 cursor-pointer transition-colors duration-200 group"
                      onClick={() => handleReporteClick('https://drive.google.com/drive/folders/1UwEE5e-ozg58SmcY18OBUwj-rMDb36AU?usp=sharing')}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 group-hover:bg-gray-200 transition-colors">
                            <FolderOpenIcon />
                          </div>
                          <div className="space-y-1">
                            <h3 className="font-semibold text-black">Q2 2025</h3>
                            <p className="text-sm text-muted-foreground">
                              Abril - Junio 2025
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-sm text-gray-500">PDF & Excel</span>
                          <ArrowUpRightIcon />
                        </div>
                      </div>
                    </div>

                    {/* Fila 2024 */}
                    <div 
                      className="p-6 hover:bg-gray-50 cursor-pointer transition-colors duration-200 group"
                      onClick={() => handleReporteClick('https://drive.google.com/drive/folders/1Ubx13p6tUSU2c0LzuzmMs4CTsWiG8SqO?usp=sharing')}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 group-hover:bg-gray-200 transition-colors">
                            <FolderOpenIcon />
                          </div>
                          <div className="space-y-1">
                            <h3 className="font-semibold text-black">
                              {getBilingualText("2024")}
                            </h3>
                            <p className="text-sm text-muted-foreground">
                              {getBilingualText("")}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-sm text-gray-500">PDF & Excel</span>
                          <ArrowUpRightIcon />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  };

  const renderOperativo = () => {
    const handleReporteClick = (url: string) => {
      window.open(url, '_blank');
    };

    return (
      <div className="min-h-screen bg-gray-50">
        <div className="p-6 space-y-8">
          {/* Header fijo */}
          <div className="space-y-2">
            <h1 className="text-2xl font-semibold text-black">
              {getBilingualText("reports.operational.title")}
            </h1>
            <p className="text-muted-foreground">
              {getBilingualText("reports.operational.subtitle")}
            </p>
          </div>

          {/* Tabla de reportes */}
          <div className="space-y-6">
            <Card className="bg-white rounded-2xl border border-gray-100 shadow-sm">
              <CardContent className="p-0">
                <div className="overflow-hidden">
                  {/* Header */}
                  <div className="bg-gray-50 p-6 border-b border-gray-100">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-50 to-green-100 flex items-center justify-center flex-shrink-0">
                        <CogIcon />
                      </div>
                      <div>
                        <h2 className="text-xl font-semibold text-black">
                          {getBilingualText("reports.operational.title")}
                        </h2>
                        <p className="text-muted-foreground">
                          Próximo reporte: 15 de noviembre de 2025, 08.00 am CET
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Tabla */}
                  <div className="divide-y divide-gray-100">
                    {/* Fila Reporte Operativo Q2 2025 */}
                    <div 
                      className="p-6 hover:bg-gray-50 cursor-pointer transition-colors duration-200 group"
                      onClick={() => handleReporteClick('https://drive.google.com/file/d/1fIsEbdiMMhQo6ltSOxN2WhqzzRSvqxWk/view?usp=sharing')}
                    >
                      <div className="flex items-center gap-6">
                        {/* Video Thumbnail */}
                        <div className="relative flex-shrink-0">
                          <div className="w-48 h-28 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-xl overflow-hidden shadow-lg group-hover:shadow-xl transition-shadow duration-200">
                            {/* Background pattern */}
                            <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 to-purple-600/20"></div>
                            
                            {/* Content */}
                            <div className="absolute inset-0 flex flex-col justify-center items-center text-white p-4">
                              <div className="text-center space-y-2">
                                {/* Bellamy Logo/Icon */}
                                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-2">
                                  <span className="text-xs font-bold text-white">B&P</span>
                                </div>
                                <div className="text-xs font-semibold text-white/90 leading-tight">
                                  BELLAMY & PARTNERS
                                </div>
                                <div className="text-xs text-white/80 leading-tight">
                                  Report to Shareholders
                                </div>
                              </div>
                            </div>
                            
                            {/* Play Button */}
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center shadow-lg group-hover:bg-red-700 transition-colors duration-200">
                                <svg width="14" height="16" viewBox="0 0 24 24" fill="white" className="ml-1">
                                  <path d="M8 5v14l11-7z"/>
                                </svg>
                              </div>
                            </div>
                            
                            {/* Duration badge */}
                            <div className="absolute bottom-2 right-2 bg-black/80 text-white text-xs px-2 py-1 rounded-md">
                              15:42
                            </div>
                          </div>
                        </div>

                        {/* Content */}
                        <div className="flex-1 space-y-2">
                          <div className="flex items-start justify-between">
                            <div className="space-y-1 flex-1">
                              <h3 className="font-semibold text-black text-lg group-hover:text-blue-600 transition-colors duration-200">
                                Bellamy & Partners Report to Shareholders
                              </h3>
                              <p className="text-sm text-muted-foreground">
                                Q2 2025 Operational Review • Análisis operativo segundo trimestre
                              </p>
                              <div className="flex items-center gap-4 text-xs text-gray-500 mt-2">
                                <span className="flex items-center gap-1">
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <polyline points="12,6 12,12 16,14"></polyline>
                                  </svg>
                                  15:42 min
                                </span>
                                <span className="flex items-center gap-1">
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                    <circle cx="12" cy="12" r="3"></circle>
                                  </svg>
                                  Video Report
                                </span>
                                <span className="flex items-center gap-1">
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                                    <line x1="16" y1="2" x2="16" y2="6"></line>
                                    <line x1="8" y1="2" x2="8" y2="6"></line>
                                    <line x1="3" y1="10" x2="21" y2="10"></line>
                                  </svg>
                                  Jun 2025
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 ml-4">
                              <ArrowUpRightIcon />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Fila Todos los reportes */}
                    <div 
                      className="p-6 hover:bg-gray-50 cursor-pointer transition-colors duration-200 group"
                      onClick={() => handleReporteClick('https://drive.google.com/drive/folders/1e4n5jo896nHofnZaJJj15-05NPx8DY0y?usp=sharing')}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 group-hover:bg-gray-200 transition-colors">
                            <FolderOpenIcon />
                          </div>
                          <div className="space-y-1">
                            <h3 className="font-semibold text-black">
                              Previous Reports
                            </h3>
                            <p className="text-sm text-muted-foreground">
                                  
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-sm text-gray-500">Drive Folder</span>
                          <ArrowUpRightIcon />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  };

  const renderDocumentacion = () => {
    const handleDocumentoClick = (url: string) => {
      window.open(url, '_blank');
    };

    return (
      <div className="min-h-screen bg-gray-50">
        <div className="p-6 space-y-8">
          {/* Header fijo */}
          <div className="space-y-2">
            <h1 className="text-2xl font-semibold text-black">
              {getBilingualText("reports.documentation.title")}
            </h1>
            <p className="text-muted-foreground">
              {getBilingualText("reports.documentation.subtitle")}
            </p>
          </div>

          {/* Grid de documentos */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Governanza */}
            <div 
              className="p-6 hover:bg-gray-50 cursor-pointer transition-colors duration-200 group border border-gray-200 rounded-2xl bg-white shadow-sm hover:shadow-md"
              onClick={() => handleDocumentoClick('https://drive.google.com/drive/folders/1HbHHGI-K5oJvFBxo6z9qj1l0tDY4BN9T?usp=sharing')}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 group-hover:bg-gray-200 transition-colors">
                    <ShieldIcon />
                  </div>
                  <div className="space-y-1">
                    <h3 className="font-semibold text-black">
                      {getBilingualText("docs.governance.title")}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {getBilingualText("docs.governance.subtitle")}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-sm text-gray-500">Folder</span>
                  <ArrowUpRightIcon />
                </div>
              </div>
            </div>

            {/* Ampliaciones de Capital */}
            <div 
              className="p-6 hover:bg-gray-50 cursor-pointer transition-colors duration-200 group border border-gray-200 rounded-2xl bg-white shadow-sm hover:shadow-md"
              onClick={() => handleDocumentoClick('https://drive.google.com/drive/folders/1K7JZcX9hH9fFfDnNdQF1-F8kE5BrZMV7?usp=sharing')}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 group-hover:bg-gray-200 transition-colors">
                    <FileTextIcon />
                  </div>
                  <div className="space-y-1">
                    <h3 className="font-semibold text-black">
                      {language === "en" ? "Capital increases" : "Ampliaciones de Capital"}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {getBilingualText("docs.capitalExtensions.subtitle")}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-sm text-gray-500">Folder</span>
                  <ArrowUpRightIcon />
                </div>
              </div>
            </div>

            {/* Impuestos */}
            <div 
              className="p-6 hover:bg-gray-50 cursor-pointer transition-colors duration-200 group border border-gray-200 rounded-2xl bg-white shadow-sm hover:shadow-md"
              onClick={() => handleDocumentoClick('https://drive.google.com/drive/folders/1N8xKc2vFv5fK9KfVfE8gH7J8fD5FdC3V?usp=sharing')}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 group-hover:bg-gray-200 transition-colors">
                    <ReceiptIcon />
                  </div>
                  <div className="space-y-1">
                    <h3 className="font-semibold text-black">
                      {getBilingualText("docs.taxes.title")}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {getBilingualText("docs.taxes.subtitle")}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-sm text-gray-500">Folder</span>
                  <ArrowUpRightIcon />
                </div>
              </div>
            </div>

            {/* Otra Documentación */}
            <div 
              className="p-6 hover:bg-gray-50 cursor-pointer transition-colors duration-200 group border border-gray-200 rounded-2xl bg-white shadow-sm hover:shadow-md"
              onClick={() => handleDocumentoClick('https://drive.google.com/drive/folders/1Q9rTw8uXz4fJ9KfVfE8gH7J8fD5FdC3V?usp=sharing')}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 group-hover:bg-gray-200 transition-colors">
                    <FolderOpenIcon />
                  </div>
                  <div className="space-y-1">
                    <h3 className="font-semibold text-black">
                      {getBilingualText("docs.other.title")}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {getBilingualText("docs.other.subtitle")}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-sm text-gray-500">Folder</span>
                  <ArrowUpRightIcon />
                </div>
              </div>
            </div>
          </div>

          {/* Información adicional */}
          <Card className="bg-white rounded-2xl border border-gray-100 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                  </svg>
                </div>
                <div className="space-y-1">
                  <h3 className="font-semibold text-gray-900">
                    {getBilingualText("docs.secure.title")}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {getBilingualText("docs.secure.subtitle")}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  };

  // Navegación principal de reportes
  if (currentPage === "reportes-financiero") {
    return renderFinanciero();
  }

  if (currentPage === "reportes-operativo") {
    return renderOperativo();
  }

  if (currentPage === "reportes-documentacion") {
    return renderDocumentacion();
  }

  // Vista principal con tabs
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-6 space-y-8">
        {/* Header */}
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold text-black">
            {getBilingualText("reports.title")}
          </h1>
          <p className="text-muted-foreground">
            Centro de reportes e información
          </p>
        </div>

        <Tabs defaultValue="financiero" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="financiero">
              {getBilingualText("reports.tabs.financial")}
            </TabsTrigger>
            <TabsTrigger value="operativo">
              {getBilingualText("reports.tabs.operational")}
            </TabsTrigger>
            <TabsTrigger value="documentacion">
              {getBilingualText("reports.tabs.documentation")}
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="financiero">
            {renderFinanciero()}
          </TabsContent>
          
          <TabsContent value="operativo">
            {renderOperativo()}
          </TabsContent>
          
          <TabsContent value="documentacion">
            {renderDocumentacion()}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}